import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { EmployeeAccessStep } from "@components/request-access/employee/EmployeeAccessStep";
import { EmployeeFormData } from "@components/request-access/employee-model";
import { EmployeeWarningStep } from "@components/request-access/employee/EmployeeWarningStep";
import { GetStaticPropsContext } from "next";
import Head from "next/head";
import { RequestSent } from "@components/request-access/RequestSent";
import { RequestedPortalStep } from "@components/request-access/RequestedPortalStep";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Wrapper } from "@components/request-access/Wrapper";
import { getRequestAccessEndpoint } from "@components/request-access/request-access-api";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useEmployeeAccessFormik } from "@components/request-access/useEmployeeAccessFormik";
import { useRequestedPortalFormik } from "@components/request-access/useRequestedPortalFormik";
import { useStepManager } from "../../lib/stepper/useStepManager";
import { useTranslation } from "next-i18next";

export default function RequestAccessEmployeePage() {
  const { t } = useTranslation();

  const steps = [
    t("request_access:employee_access_warning.title"),
    t("request_access:employee_access.portal_request"),
    t("request_access:employee_access.employee_information"),
  ];

  const { activeStep, completed, allStepsCompleted, handleComplete } =
    useStepManager(steps);

  const requestedPortalFormik = useRequestedPortalFormik(() => {
    handleComplete();
  });

  const employeeAccessFormik = useEmployeeAccessFormik(async () => {
    // Employee email validation
    const result = await validateEmployee();
    if (result) {
      handleSubmission();
      employeeAccessFormik.setFieldValue("validEmployee", true);
    } else {
      employeeAccessFormik.setFieldValue("validEmployee", false);
    }
  });

  async function handleSubmission() {
    // TODO consider loading indicators
    const result = await submitRequest();
    if (result) {
      handleComplete();
    } else {
      // TODO error handling
      // what should we present to the user
      // they will still be on step 3
      // so we will need a way to send data down to that component,
      // or have it bump down
    }
  }

  async function submitRequest() {
    const frontEndHostName =
      typeof window !== "undefined" ? window.location.host : "ssr";

    const form: EmployeeFormData = {
      ...employeeAccessFormik.values,
      ...requestedPortalFormik.values,
    };

    const payload: EmployeeFormData & {
      frontEndHostName: string;
      accessType: "employee";
    } = {
      ...form,
      frontEndHostName: frontEndHostName,
      accessType: "employee",
    };

    console.info("🛫 sending employee request access payload", payload);

    const result = await postRequest(payload);

    if (result) {
      console.info("submit request successful");
      return true;
    }

    console.info("submit request failure");
    return false;
  }

  async function postRequest(payload = {}) {
    const postEndpoint = getRequestAccessEndpoint();
    const response = await fetch(postEndpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      referrerPolicy: "no-referrer",
      body: JSON.stringify(payload),
    });

    const result = response.status >= 200 && response.status <= 204;

    return result;
  }

  const validateEmployee = async () => {
    const payload = buildEmployeeValidationPayload();
    const result = await postEmployeeValidation(payload);

    if (result) {
      return true; // Employee Validation Successful
    }

    return false; // Employee Validation Failure
  };

  const buildEmployeeValidationPayload = () => {
    return {
      dsId: employeeAccessFormik.values.dsId,
      email: employeeAccessFormik.values.email,
      firstName: "",
      lastName: "",
      cargillRole: "",
      cargillBusinessEmployee: "",
      employeeRegion: "",
      appRefName: "",
      empEmail: true,
    };
  };

  async function postEmployeeValidation(payload = {}) {
    const postEndpoint = getRequestAccessEndpoint();
    const response = await fetch(postEndpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      referrerPolicy: "no-referrer",
      body: JSON.stringify(payload),
    });
    const result = response.status >= 200 && response.status <= 204;

    return result;
  }

  return (
    <>
      <Head>
        <title>{`myCargill - ${t(
          "request_access:select_access_type.employee_title"
        )} - ${t("common:header.get_access")}`}</title>
        <meta
          name="description"
          content={t("common:header.get_access") || undefined}
        />
      </Head>
      <Wrapper
        top={
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: "3rem",
            }}
          >
            <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Container>
        }
      >
        <Box sx={{ width: "100%" }}>
          <div>
            {allStepsCompleted() ? (
              <>
                <RequestSent />
              </>
            ) : (
              <Box sx={{ mt: 2, mb: 1, py: 1 }}>
                {/* put conditional content in this box */}

                {/* first step screen */}
                {activeStep === 0 ? (
                  <EmployeeWarningStep onNext={handleComplete} />
                ) : null}

                {/* second step screen */}
                {activeStep === 1 ? (
                  <RequestedPortalStep formik={requestedPortalFormik} />
                ) : null}

                {/* third/final step screen */}
                {activeStep === 2 ? (
                  <EmployeeAccessStep formik={employeeAccessFormik} />
                ) : null}
              </Box>
            )}
          </div>
        </Box>
      </Wrapper>
    </>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale as string, [
        "common",
        "request_access",
      ])),
    },
  };
}
